/**
 * Spec 003 — auth fetch wrappers. Same discriminated-union result shape as lib/health.ts / membership:
 * these NEVER throw; each maps to an explicit outcome the UI renders. All requests send cookies
 * (`credentials: 'include'`); mutating routes that require it echo the double-submit CSRF token.
 */

const CLIENT_TIMEOUT_MS = 10_000;

export type FieldErrors = Record<string, string>;

export type RequestLinkResult =
  | { kind: 'ok' }
  | { kind: 'invalid'; errors: FieldErrors }
  | { kind: 'rate_limited' }
  | { kind: 'error' };

export type LoginResult =
  | { kind: 'ok' }
  | { kind: 'invalid'; errors: FieldErrors }
  | { kind: 'rejected'; message: string }
  | { kind: 'locked'; message: string }
  | { kind: 'rate_limited' }
  | { kind: 'error' };

export type SetPasswordResult =
  | { kind: 'ok' }
  | { kind: 'invalid'; errors: FieldErrors }
  | { kind: 'error' };

/** Read the non-httpOnly CSRF token cookie so it can be echoed in the X-CSRF-Token header. */
export function readCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)oi_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

async function postJson(
  path: string,
  body: unknown,
  opts: { csrf?: boolean } = {},
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CLIENT_TIMEOUT_MS);
  const headers: Record<string, string> = { 'content-type': 'application/json', accept: 'application/json' };
  if (opts.csrf) {
    const token = readCsrfToken();
    if (token) headers['x-csrf-token'] = token;
  }
  try {
    return await fetch(path, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

export async function requestMagicLink(email: string): Promise<RequestLinkResult> {
  try {
    const res = await postJson('/api/auth/request-link', { email });
    if (res.ok) return { kind: 'ok' };
    if (res.status === 429) return { kind: 'rate_limited' };
    if (res.status === 400) {
      const body = (await res.json().catch(() => ({}))) as { errors?: FieldErrors };
      return { kind: 'invalid', errors: body.errors ?? {} };
    }
    return { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}

export async function verifyMagicLink(token: string): Promise<{ kind: 'ok' } | { kind: 'invalid' } | { kind: 'rate_limited' } | { kind: 'error' }> {
  try {
    const res = await postJson('/api/auth/verify', { token });
    if (res.ok) return { kind: 'ok' };
    if (res.status === 429) return { kind: 'rate_limited' };
    if (res.status === 401) return { kind: 'invalid' };
    return { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}

export async function passwordLogin(email: string, password: string): Promise<LoginResult> {
  try {
    const res = await postJson('/api/auth/password-login', { email, password });
    if (res.ok) return { kind: 'ok' };
    if (res.status === 429) return { kind: 'rate_limited' };
    if (res.status === 423) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      return { kind: 'locked', message: body.error ?? 'Too many attempts. Try again later.' };
    }
    if (res.status === 401) {
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      return { kind: 'rejected', message: body.message ?? 'Email or password is incorrect.' };
    }
    if (res.status === 400) {
      const body = (await res.json().catch(() => ({}))) as { errors?: FieldErrors };
      return { kind: 'invalid', errors: body.errors ?? {} };
    }
    return { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}

export async function setPassword(password: string): Promise<SetPasswordResult> {
  try {
    const res = await postJson('/api/auth/set-password', { password }, { csrf: true });
    if (res.ok) return { kind: 'ok' };
    if (res.status === 400) {
      const body = (await res.json().catch(() => ({}))) as { errors?: FieldErrors };
      return { kind: 'invalid', errors: body.errors ?? {} };
    }
    return { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}
